import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as faceapi from "face-api.js";
import { apiClient } from "../services/api";
import DialPad from "../components/DialPad";
import { type Student } from "../types/Student";

type DisplayStudent = Student & {
  attendance?: string | null;
  timestamp?: string | Date;
};

function ViewerPage() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const [studentNumber, setStudentNumber] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [matchedStudent, setMatchedStudent] = useState<DisplayStudent | null>(null);
  const [resultStudent, setResultStudent] = useState<DisplayStudent | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">(""); // success, error, or empty
  const [messageOpacity, setMessageOpacity] = useState(1);
  const [studentBoxOpacity, setStudentBoxOpacity] = useState(1);
  
  const [faceRecognitionEnabled, setFaceRecognitionEnabled] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [faceDescriptors, setFaceDescriptors] = useState<{ studentNumber: string; descriptor: Float32Array }[]>([]);
  const [lastRecognitionTime, setLastRecognitionTime] = useState<number>(0);

  useEffect(() => {
    const loadStudents = async () => {
      try {
        const list = await apiClient.getStudents();
        setStudents(list);
      } catch (error) {
        console.error("학생 목록 불러오기 실패:", error);
        setMessage("학생 목록을 불러오지 못했습니다.");
        setMessageType("error");
      }
    };

    loadStudents();
  }, []);

  // face-api.js 모델 로드
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = "/models";
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        setModelsLoaded(true);
        console.log("✅ 얼굴 인식 모델 로드 완료");
      } catch (error) {
        console.error("모델 로드 실패:", error);
      }
    };

    loadModels();
  }, []);

  // 등록된 얼굴 디스크립터 로드
  useEffect(() => {
    const loadFaceDescriptors = async () => {
      try {
        const response = await apiClient.getAllFaceDescriptors();
        const descriptors = response.map((item: any) => ({
          studentNumber: item.studentNumber,
          descriptor: new Float32Array(item.descriptor)
        }));
        setFaceDescriptors(descriptors);
        console.log(`✅ ${descriptors.length}명의 얼굴 데이터 로드 완료`);
      } catch (error) {
        console.error("얼굴 디스크립터 로드 실패:", error);
      }
    };

    if (modelsLoaded) {
      loadFaceDescriptors();
    }
  }, [modelsLoaded]);

  // 카메라 시작/중지
  useEffect(() => {
    if (faceRecognitionEnabled && modelsLoaded) {
      startCamera();
      startFaceDetection();
    } else {
      stopCamera();
      stopFaceDetection();
    }

    return () => {
      stopCamera();
      stopFaceDetection();
    };
    // eslint-disable-next-line
  }, [faceRecognitionEnabled, modelsLoaded]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play().catch(err => {
              console.error("비디오 재생 실패:", err);
            });
          }
        };
      }
    } catch (error: any) {
      console.error("카메라 접근 실패:", error);
      let errorMessage = "카메라에 접근할 수 없습니다.";
      
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        errorMessage = "카메라 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.";
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        errorMessage = "카메라를 찾을 수 없습니다. 카메라가 연결되어 있는지 확인해주세요.";
      } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
        errorMessage = "카메라가 다른 앱에서 사용 중입니다. 다른 앱을 종료하고 다시 시도해주세요.";
      } else if (error.name === "OverconstrainedError" || error.name === "ConstraintNotSatisfiedError") {
        errorMessage = "카메라 설정이 지원되지 않습니다.";
      } else if (error.name === "NotSupportedError") {
        errorMessage = "HTTPS 연결이 필요합니다. 주소창에서 https://로 접속해주세요.";
      }
      
      setMessage(errorMessage);
      setMessageType("error");
      setFaceRecognitionEnabled(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const startFaceDetection = () => {
    if (detectionIntervalRef.current) return;

    detectionIntervalRef.current = setInterval(async () => {
      await detectAndRecognizeFace();
    }, 1000); // 1초마다 감지
  };

  const stopFaceDetection = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
  };

  const detectAndRecognizeFace = async () => {
    if (!videoRef.current || !canvasRef.current || !faceRecognitionEnabled) return;

    // 5초 쿨다운 체크
    const now = Date.now();
    if (now - lastRecognitionTime < 5000) {
      return;
    }

    try {
      // 모든 얼굴 감지 (여러 사람 동시 처리)
      const detections = await faceapi
        .detectAllFaces(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.6 })
        )
        .withFaceLandmarks()
        .withFaceDescriptors();

      // 캔버스 클리어
      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }

      if (detections.length > 0) {
        // 거리 필터링: 얼굴 크기로 거리 판단 (약 1m 이내 = 얼굴 너비 200px 이상)
        const closeDetections = detections.filter(detection => {
          const faceWidth = detection.detection.box.width;
          return faceWidth >= 200; // 더 근접한 얼굴만 허용
        });

        if (closeDetections.length === 0) {
          // 가까운 사람 없음
          return;
        }

        // 가장 큰 얼굴(가장 가까운 사람) 선택
        const closestDetection = closeDetections.reduce((prev, current) => {
          return (current.detection.box.width > prev.detection.box.width) ? current : prev;
        });

        // 캔버스에 감지된 얼굴 표시 (가장 가까운 사람만)
        const displaySize = {
          width: videoRef.current.videoWidth,
          height: videoRef.current.videoHeight
        };
        faceapi.matchDimensions(canvasRef.current, displaySize);
        const resizedDetection = faceapi.resizeResults(closestDetection, displaySize);
        
        if (ctx) {
          faceapi.draw.drawDetections(canvasRef.current, resizedDetection);
          faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetection);
        }

        // 얼굴 매칭
        const currentDescriptor = closestDetection.descriptor;
        let bestMatch: { studentNumber: string; distance: number } | null = null;

        for (const stored of faceDescriptors) {
          const distance = faceapi.euclideanDistance(currentDescriptor, stored.descriptor);
          if (!bestMatch || distance < bestMatch.distance) {
            bestMatch = { studentNumber: stored.studentNumber, distance };
          }
        }

        // 임계값 0.4 이하면 매칭 성공 (정밀도 강화)
        if (bestMatch && bestMatch.distance < 0.4) {
          console.log(`✅ 얼굴 인식 성공: ${bestMatch.studentNumber} (거리: ${bestMatch.distance.toFixed(3)}, 얼굴크기: ${closestDetection.detection.box.width.toFixed(0)}px)`);
          setStudentNumber(bestMatch.studentNumber);
          updateMatchedStudent(bestMatch.studentNumber);
          
          // 인식 성공 메시지
          setMessage(`${bestMatch.studentNumber} 학생 인식됨`);
          setMessageType("success");
          
          // 3초 쿨다운 시작
          setLastRecognitionTime(now);
        }
      }
    } catch (error) {
      console.error("얼굴 감지 실패:", error);
    }
  };

  const toggleFaceRecognition = () => {
    if (!modelsLoaded) {
      setMessage("얼굴 인식 모델을 로드하는 중입니다...");
      setMessageType("error");
      return;
    }
    if (faceDescriptors.length === 0) {
      setMessage("등록된 얼굴이 없습니다.");
      setMessageType("error");
      return;
    }
    setFaceRecognitionEnabled(!faceRecognitionEnabled);
  };

  // 메시지 자동 fade out (5초 후)
  useEffect(() => {
    if (message) {
      setMessageOpacity(1);
      
      const fadeTimer = setTimeout(() => {
        setMessageOpacity(0);
      }, 5000);

      const clearTimer = setTimeout(() => {
        setMessage("");
        setMessageType("");
      }, 5500); // fade out 완료 후 제거

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [message]);

  // 학생 정보 박스 자동 제거 (2초 후 페이드 시작, 3초 동안 페이드아웃)
  useEffect(() => {
    if (resultStudent) {
      setStudentBoxOpacity(1);
      
      const fadeTimer = setTimeout(() => {
        setStudentBoxOpacity(0);
      }, 2000); // 2초 후 페이드 시작

      const clearTimer = setTimeout(() => {
        setStudentBoxOpacity(1); // 박스를 다시 보여준 후
        setResultStudent(null);  // 입력 대기 상태로 전환
      }, 5000); // 2초 + 3초 = 5초 후 제거

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [resultStudent]);

  const updateMatchedStudent = (value: string) => {
    if (value.length < 4) {
      setMatchedStudent(null);
      return;
    }

    const found = students.find((s) => s.studentNumber === value);
    setMatchedStudent(found ? { ...found } : null);
  };

  // 다이얼패드 입력 처리
  const handlePadClick = (val: string) => {
    if (val === "초기화") {
      setStudentNumber("");
      setMatchedStudent(null);
      setResultStudent(null);
      setMessage("");
      setMessageType("");
      return;
    }

    if (val === "삭제") {
      const next = studentNumber.slice(0, -1);
      setStudentNumber(next);
      updateMatchedStudent(next);
      // 결과 상태 초기화
      setResultStudent(null);
      setStudentBoxOpacity(1);
      return;
    }

    if (studentNumber.length >= 8) {
      return;
    }

    // 결과 상태 초기화 (새로운 입력 시작)
    setResultStudent(null);
    setStudentBoxOpacity(1);
    
    const next = studentNumber + val;
    setStudentNumber(next);
    updateMatchedStudent(next);
  };

  // 등원 처리
  const handleCheckin = async () => {
    if (!studentNumber || studentNumber.length < 4) {
      setMessage("출결번호를 4자리 이상 입력해주세요.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const result = await apiClient.checkin(studentNumber);
      setResultStudent(result);
      setMessage(`${result.name} 학생이 등원했습니다.`);
      setMessageType("success");
      setStudentNumber("");
      setMatchedStudent(null);
    } catch (error) {
      console.error("등원 처리 실패:", error);
      setMessage("등원 처리에 실패했습니다. 출결번호를 확인해주세요.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  // 하원 처리
  const handleCheckout = async () => {
    if (!studentNumber || studentNumber.length < 4) {
      setMessage("출결번호를 4자리 이상 입력해주세요.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const result = await apiClient.checkout(studentNumber);
      setResultStudent(result);
      setMessage(`${result.name} 학생이 하원했습니다.`);
      setMessageType("success");
      setStudentNumber("");
      setMatchedStudent(null);
    } catch (error) {
      console.error("하원 처리 실패:", error);
      setMessage("하원 처리에 실패했습니다. 출결번호를 확인해주세요.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const hasFaceRegistered = () => {
    if (!matchedStudent) return false;
    return faceDescriptors.some(fd => fd.studentNumber === matchedStudent.studentNumber);
  };

  const handleFaceRegister = () => {
    if (!studentNumber || studentNumber.length < 4) {
      setMessage("출결번호를 4자리 이상 입력해주세요.");
      setMessageType("error");
      return;
    }
    navigate(`/face-register?studentNumber=${studentNumber}`);
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "#f8fafc",
      overflow: "hidden",
      display: "flex",
      flexDirection: "row",
      alignItems: "stretch",
    }}>
      {/* 왼쪽: 결과 화면 */}
      <div style={{
        width: "50vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8fafc",
        overflow: "hidden",
        padding: "20px",
        position: "relative"
      }}>
        {/* 얼굴 인식/등록 버튼 (top left) */}
        <div style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          display: "flex",
          gap: "12px",
          zIndex: 10
        }}>
          <button
            onClick={toggleFaceRecognition}
            style={{
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: "600",
              color: "#fff",
              background: faceRecognitionEnabled ? "#dc2626" : "#2563eb",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {faceRecognitionEnabled ? "📷 인식 중지" : "📷 얼굴 인식"}
          </button>
          
          <button
            onClick={(e) => {
              if (!studentNumber || studentNumber.length < 4) {
                // 비활성화 상태에서 클릭 시 도움말 표시
                const tooltip = document.createElement("div");
                tooltip.textContent = "출결번호를 입력 후 클릭";
                tooltip.style.cssText = `
                  position: fixed;
                  background: #222;
                  color: #fff;
                  padding: 8px 12px;
                  borderRadius: 6px;
                  fontSize: 12px;
                  zIndex: 1000;
                  pointerEvents: none;
                  whiteSpace: nowrap;
                  animation: fadeInOut 5s ease-in-out forwards;
                `;
                const rect = e.currentTarget.getBoundingClientRect();
                tooltip.style.top = (rect.top - 35) + "px";
                tooltip.style.left = (rect.left + rect.width / 2 - 65) + "px";
                document.body.appendChild(tooltip);
                
                // CSS 애니메이션 정의
                if (!document.getElementById("tooltip-animation")) {
                  const style = document.createElement("style");
                  style.id = "tooltip-animation";
                  style.textContent = `
                    @keyframes fadeInOut {
                      0% { opacity: 1; }
                      80% { opacity: 1; }
                      100% { opacity: 0; }
                    }
                  `;
                  document.head.appendChild(style);
                }
                
                setTimeout(() => {
                  if (tooltip.parentNode) {
                    tooltip.parentNode.removeChild(tooltip);
                  }
                }, 5000);
              } else {
                // 활성화 상태에서 클릭 시 등록 페이지로 이동
                handleFaceRegister();
              }
            }}
            disabled={!studentNumber || studentNumber.length < 4}
            style={{
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: "600",
              color: (studentNumber && studentNumber.length >= 4) ? "#fff" : "#a5b4fc",
              background: (studentNumber && studentNumber.length >= 4) ? "#8b5cf6" : "#e0e7ff",
              border: "none",
              borderRadius: "8px",
              cursor: (studentNumber && studentNumber.length >= 4) ? "pointer" : "not-allowed",
              boxShadow: (studentNumber && studentNumber.length >= 4) ? "0 2px 8px rgba(139, 92, 246, 0.3)" : "none",
              transition: "all 0.2s",
              position: "relative"
            }}
            onMouseEnter={(e) => {
              if (studentNumber && studentNumber.length >= 4) {
                e.currentTarget.style.transform = "scale(1.05)";
              } else {
                // 비활성화 상태일 때 도움말 표시
                const tooltip = document.createElement("div");
                tooltip.textContent = "출결번호를 입력 후 클릭";
                tooltip.style.cssText = `
                  position: fixed;
                  background: #222;
                  color: #fff;
                  padding: 8px 12px;
                  borderRadius: 6px;
                  fontSize: 12px;
                  zIndex: 1000;
                  pointerEvents: none;
                  whiteSpace: nowrap;
                `;
                const rect = e.currentTarget.getBoundingClientRect();
                tooltip.style.top = (rect.top - 35) + "px";
                tooltip.style.left = (rect.left + rect.width / 2 - 65) + "px";
                document.body.appendChild(tooltip);
                
                (e.currentTarget as any).tooltipElement = tooltip;
                (e.currentTarget as any).tooltipTimer = setTimeout(() => {
                  if (tooltip.parentNode) {
                    tooltip.parentNode.removeChild(tooltip);
                  }
                }, 5000);
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              if ((e.currentTarget as any).tooltipElement) {
                const tooltip = (e.currentTarget as any).tooltipElement;
                if (tooltip.parentNode) {
                  tooltip.parentNode.removeChild(tooltip);
                }
              }
              if ((e.currentTarget as any).tooltipTimer) {
                clearTimeout((e.currentTarget as any).tooltipTimer);
              }
            }}
          >
            📸 {hasFaceRegistered() ? "얼굴 재등록" : "얼굴 등록"}
          </button>
        </div>

        {/* 카메라 미리보기 */}
        {faceRecognitionEnabled && (
          <div style={{
            position: "absolute",
            top: "80px",
            left: "20px",
            width: "calc(50vw - 40px)",
            height: "calc(100vh - 160px)",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
            background: "#000",
            zIndex: 10
          }}>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "scaleX(-1)" // 좌우 반전
              }}
            />
            <canvas
              ref={canvasRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                transform: "scaleX(-1)" // 좌우 반전
              }}
            />
          </div>
        )}

      </div>

      {/* 오른쪽: 다이얼패드 */}
      <DialPad
        studentNumber={studentNumber}
        onPadClick={handlePadClick}
        onCheckin={handleCheckin}
        onCheckout={handleCheckout}
        loading={loading}
        matchedStudent={matchedStudent}
        resultStudent={resultStudent}
        studentBoxOpacity={studentBoxOpacity}
      />
    </div>
  );
}

export default ViewerPage;
